import Grid from '@mui/material/Grid';
import React from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { isBlankString, isValidEmail, isValidPhone } from 'shared/utils';
import { TextEditor } from 'shared/components/data-display/editors';
import { TooltipIconButton } from 'shared/components/abstract/interactables';

// Function to enforce valid data changes for contact info
export function hasValidContactInformationUpdates(data, updateData) {
  const {
    ticket_contact: ticketContact,
  } = data;
  const {
    name,
    email,
    phone,
  } = ticketContact || {};
  const {
    name: updatedName,
    email: updatedEmail,
    phone: updatedPhone,
  } = updateData;

  const updatedNamePresent = 'name' in updateData;
  const updatedEmailPresent = 'email' in updateData;
  const updatedPhonePresent = 'phone' in updateData;

  const nameIsBlank = isBlankString(name);
  const emailIsBlank = isBlankString(email);
  const phoneIsBlank = isBlankString(phone);

  const updatedNameIsBlank = isBlankString(updatedName);
  const updatedEmailIsBlank = isBlankString(updatedEmail);
  const updatedPhoneIsBlank = isBlankString(updatedPhone);

  //   Needs valid updates
  if (nameIsBlank && emailIsBlank && phoneIsBlank
    && !(updatedNamePresent && (updatedEmailPresent || updatedPhonePresent))) {
    return false;
  }

  // Updates to name must maintain valid name
  if (updatedNamePresent && updatedNameIsBlank) {
    return false;
  }

  // If we only have an email that's being removed, ensure we get a phone
  if (!emailIsBlank && phoneIsBlank && updatedEmailPresent
    && updatedEmailIsBlank && updatedPhoneIsBlank) {
    return false;
  }

  // If we only have a phone that's being removed, ensure we get an email
  if (!phoneIsBlank && emailIsBlank && updatedPhonePresent
    && updatedPhoneIsBlank && updatedEmailIsBlank) {
    return false;
  }

  // If we have both, ensure we don't remove both
  if (!emailIsBlank && !phoneIsBlank && updatedEmailPresent && updatedPhonePresent
    && updatedEmailIsBlank && updatedPhoneIsBlank) {
    return false;
  }

  // If we don't have either contact, ensure we get one
  if (emailIsBlank && phoneIsBlank && updatedEmailIsBlank && updatedPhoneIsBlank) {
    return false;
  }

  // Ensure the updated contact values are valid
  if (updatedEmailPresent && !updatedEmailIsBlank && !isValidEmail(updatedEmail)) {
    return false;
  }
  return !(updatedPhonePresent && !updatedPhoneIsBlank && !isValidPhone(updatedPhone));
}

const ticketContactDescriptions = [
  {
    label: 'Name',
    key: 'name',
    editor: TextEditor,
  },
  {
    label: 'Email',
    key: 'email',
    editor: TextEditor,
  },
  {
    label: 'Phone',
    key: 'phone',
    editor: TextEditor,
  },
];

/**
 * @param {Object} props
 * @param {Object} [props.data={ name: '', phone: '', email: '' }]
 * @param {Object} [props.updatedData={ readOnly: true }]
 * @param {Function} props.handleChange
 * @param {boolean} [props.mediaQuery=false]
 */
export default function ContactInformation(props) {
  const {
    handleChange,
    data = { name: '', phone: '', email: '' },
    updatedData = { readOnly: true },
    mediaQuery = false,
  } = props;

  const [open, setOpen] = React.useState(!mediaQuery);

  function expandCollapseIcon() {
    return (
      open
        ? (
          <TooltipIconButton
            className="entry-icon"
            message="Collapse text"
            icon={ExpandLessIcon}
            onClick={() => setOpen(!open)}
          />
        )
        : (
          <TooltipIconButton
            className="entry-icon"
            message="Expand text"
            icon={ExpandMoreIcon}
            onClick={() => setOpen(!open)}
          />
        )
    );
  }

  return (
    <Grid lg={12}>
      <div className="item-child-title">
        Ticket Contact
        {expandCollapseIcon()}
      </div>
      <Collapse in={open} timeout="auto">
        <DataFormatter
          editing
          data={data}
          descriptions={ticketContactDescriptions}
          updatedData={updatedData}
          updateValue={handleChange}
        />
      </Collapse>
    </Grid>
  );
}
